import type {
  AlarmEvent,
  LiveEvent,
  MachineUpdateEvent,
  SnapshotEvent,
} from '@bridgewerk/shared';

export type WsConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface WsEvents {
  snapshot: (event: SnapshotEvent) => void;
  machine_update: (event: MachineUpdateEvent) => void;
  alarm: (event: AlarmEvent) => void;
  state: (state: WsConnectionState) => void;
  error: (err: Error) => void;
}

export type WsEventName = keyof WsEvents;

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 32_000, 60_000];
/** Auth-failure close codes — fail fast, do not retry. */
const FATAL_CLOSE_CODES = new Set([4401, 4403]);

export type WebSocketCtor = new (url: string) => WebSocket;

export interface WsClientDeps {
  readonly shopId: string;
  readonly baseUrl: string;
  readonly getToken: () => Promise<string | null> | string | null;
  /** Test seam — defaults to the global WebSocket. */
  readonly WebSocketCtor?: WebSocketCtor;
  /** Test seam — defaults to setTimeout. */
  readonly setTimeout?: typeof setTimeout;
  readonly clearTimeout?: typeof clearTimeout;
}

export class WsClient {
  public state: WsConnectionState = 'connecting';

  private readonly listeners: { [K in WsEventName]: Set<WsEvents[K]> } = {
    snapshot: new Set(),
    machine_update: new Set(),
    alarm: new Set(),
    state: new Set(),
    error: new Set(),
  };
  private socket: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private readonly Ctor: WebSocketCtor;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;

  public constructor(private readonly deps: WsClientDeps) {
    this.Ctor = deps.WebSocketCtor ?? (globalThis.WebSocket as unknown as WebSocketCtor);
    this.setTimeoutFn = deps.setTimeout ?? globalThis.setTimeout.bind(globalThis);
    this.clearTimeoutFn = deps.clearTimeout ?? globalThis.clearTimeout.bind(globalThis);
    void this.connect();
  }

  public on<K extends WsEventName>(event: K, handler: WsEvents[K]): () => void {
    this.listeners[event].add(handler as never);
    return () => {
      this.listeners[event].delete(handler as never);
    };
  }

  public close(): void {
    this.closed = true;
    if (this.retryTimer) {
      this.clearTimeoutFn(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, 'client closed');
      } catch {
        // ignore
      }
      this.socket = null;
    }
  }

  private async connect(): Promise<void> {
    if (this.closed) return;
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let token: string | null;
    try {
      const value = this.deps.getToken();
      token = value instanceof Promise ? await value : value;
    } catch (err) {
      this.emit('error', toError(err));
      this.scheduleReconnect();
      return;
    }

    if (this.closed) return;

    if (!token) {
      this.emit('error', new Error('no auth token available'));
      this.setState('failed');
      return;
    }

    const url = `${this.deps.baseUrl}/shops/${encodeURIComponent(
      this.deps.shopId,
    )}/live?token=${encodeURIComponent(token)}`;

    let ws: WebSocket;
    try {
      ws = new this.Ctor(url);
    } catch (err) {
      this.emit('error', toError(err));
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.setState('connected');
    };

    ws.onmessage = (msg) => {
      this.handleMessage(msg.data);
    };

    ws.onerror = () => {
      this.emit('error', new Error('websocket error'));
    };

    ws.onclose = (event) => {
      this.socket = null;
      if (this.closed) return;
      if (FATAL_CLOSE_CODES.has(event.code)) {
        this.setState('failed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let parsed: LiveEvent;
    try {
      parsed = JSON.parse(raw) as LiveEvent;
    } catch (err) {
      this.emit('error', new Error(`malformed ws message: ${stringifyError(err)}`));
      return;
    }
    if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') return;
    switch (parsed.type) {
      case 'snapshot':
        this.emit('snapshot', parsed);
        break;
      case 'machine_update':
        this.emit('machine_update', parsed);
        break;
      case 'alarm':
        this.emit('alarm', parsed);
        break;
      case 'ping':
        // Server-initiated JSON ping; nothing to do — protocol-level pong is
        // handled by the browser. Reserved for future heartbeat needs.
        break;
    }
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)];
    this.attempt += 1;
    this.setState('reconnecting');
    this.retryTimer = this.setTimeoutFn(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }

  private setState(next: WsConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.emit('state', next);
  }

  private emit<K extends WsEventName>(event: K, ...args: Parameters<WsEvents[K]>): void {
    for (const handler of this.listeners[event]) {
      try {
        (handler as (...a: unknown[]) => void)(...args);
      } catch {
        // listener errors must not break the client
      }
    }
  }
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(String(err));
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

interface RegistryEntry {
  readonly client: WsClient;
  refs: number;
}

const registry = new Map<string, RegistryEntry>();

export function getOrCreateWsClient(deps: WsClientDeps): WsClient {
  const existing = registry.get(deps.shopId);
  if (existing) {
    existing.refs += 1;
    return existing.client;
  }
  const client = new WsClient(deps);
  registry.set(deps.shopId, { client, refs: 1 });
  return client;
}

export function releaseWsClient(shopId: string): void {
  const entry = registry.get(shopId);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs <= 0) {
    entry.client.close();
    registry.delete(shopId);
  }
}

/** Test helper — wipes the singleton registry. */
export function __resetWsRegistry(): void {
  for (const { client } of registry.values()) client.close();
  registry.clear();
}
